(ns manatee.admin.task-config.documents.view
  "Re-frame components comprising the task documents admin. view. These allow
   viewing, adding and deleting the documents associated with a task."
  (:require
   [clojure.spec.alpha :as s]
   [clojure.string :as string]
   [kee-frame.core :as kf]
   [manatee.admin.components :as admin-components]
   [manatee.admin.task-config.documents.handlers :as handlers]
   [manatee.admin.task-config.documents.subs :as subs]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.components.modal.handlers :as modal-handlers]
   [manatee.components.notifications.container :as notifications-container]
   [re-frame.core :as rf]
   [rothko.button.component :as button]
   [rothko.icon.component :as icon]
   [rothko.layout.component :as layout]
   [rothko.panel.component :as panel]))

(s/def ::non-blank-string (s/and string?
                                 (complement string/blank?)))

(s/def ::link (s/and string? #(re-matches #"^[a-zA-Z]+://([^:]+(:(.+))?@)?([a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.)+([a-zA-Z]{2,63})(:(\d+))?/(([^?#]+)?(\?([^#]+)?)?(#(.+)?)?)?$" %)))

;; ==================

(defn- document-name-component
  [document-id name task-config-id]
  [:a {:style {:text-decoration :none}
       :href (kf/path-for [:api/task-document :task-config-id task-config-id :document-id document-id])
       :target "_blank"
       :download (str name ".pdf")
       :type "application/pdf"}
   name])

(defn- document-delete-component
  [document-id name task-config-id]
  [:<>
   [button/button {:appearance #{:invisible :tool-btn}
                   :on-click #(rf/dispatch [::modal-handlers/show document-id])}
    [icon/icon {:icon :bin
                :title (str "Delete " name)
                :size 14}]]
   [admin-components/confirmation-dialog {:id document-id
                                          :class [:admin-page__confirmation-dialog]
                                          :ok-fn #(do (rf/dispatch [::handlers/delete-document task-config-id document-id])
                                                      (rf/dispatch [::modal-handlers/hide document-id]))
                                          :cancel-fn #(rf/dispatch [::modal-handlers/hide document-id])}
    [:<>
     [:p (str "Completing this action will delete " name ".")]
     [:p "Are you sure?"]]]])

(defn- document-component
  [{:keys [id name] :as _document} task-config-id]
  [:tr.table__row.primary
   [:td.table__field.primary
    [document-name-component id name task-config-id]]
   [:td.table__field.primary
    [document-delete-component id name task-config-id]]])

(defn- no-documents-component
  []
  [:tr.table__row.primary
   [:td.table__field.primary
    "No data"]
   [:td.table__field.primary
    ""]])

(defn- documents-table-component
  [task-config-id]
  (let [documents @(rf/subscribe [::subs/documents])]
    [:table.primary
     [:thead.table_header.primary
      [:tr
       [:th.table__column-name-container.primary
        [:div.table__column-name.primary
         [:span
          "Name"]]]
       [:th.table__column-name-container.primary
        [:div.table__column-name.primary
         [:span
          "Delete"]]]]]
     [:tbody.table__body.primary
      (if (empty? documents)
        [no-documents-component]
        (map (fn [{:keys [id] :as document}]
               [:<> {:key id}
                [document-component document task-config-id]])
             documents))]]))

;; Document Upload
;; ===============

(defn- document-upload-submit-button-component
  [task-config-id]
  (let [{:keys [name file]} @(rf/subscribe [::subs/document-upload-payload])
        payload-valid? (and (s/valid? ::non-blank-string name)
                            file)]
    [:button.rothko-btn.secondary {:type :button
                                   :disabled (not payload-valid?)
                                   :on-click #(rf/dispatch [::handlers/upload-document task-config-id])}
     "Add"]))

(defn- document-upload-name-input-component
  []
  [:div.form__input
   [:label.form__label.primary {:for :document-name
                                :style {:display :block}}
    "Document name"]
   [:input {:type :text
            :id :document-name
            :name :document-name
            :on-change #(rf/dispatch [::handlers/set-upload-document-name (-> %
                                                                              .-target
                                                                              .-value)])}]])

(defn- document-upload-file-input-component
  []
  [:div.form__input
   [:label.form__label.primary {:for :task-document
                                :style {:display :block}}
    "Select a PDF document: "]
   [:input {:type :file
            :id :task-document
            :name :task-document
            :accept ".pdf"
            :on-change #(rf/dispatch [::handlers/set-upload-document-file (some-> %
                                                                                  .-target
                                                                                  .-files
                                                                                  (aget 0))])}]])

(defn- document-upload-form-component
  [task-config-id]
  (let [uploading? @(rf/subscribe [::subs/uploading?])]
    [:form#doc-upload-form {:action "#"} ; This is an HTML form so that it can be `reset` to clear the input field values.
     [:div.form__input
      [:fieldset {:disabled uploading?}
       [:legend
        "Add document"]
       [document-upload-name-input-component]
       [document-upload-file-input-component]
       [:br]
       [document-upload-submit-button-component task-config-id]]]]))

;; Documentation Link Upload
;; =========================

(defn- documentation-link-upload-submit-button-component
  [task-config-id]
  (let [{:keys [name link]} @(rf/subscribe [::subs/documentation-link-upload-payload])
        payload-valid? (and (s/valid? ::non-blank-string name)
                            (s/valid? ::link link))]
    [:button.rothko-btn.secondary {:type :button
                                   :disabled (not payload-valid?)
                                   :on-click #(rf/dispatch [::handlers/upload-documentation-link task-config-id])}
     "Add"]))

(defn- documentation-link-upload-name-input-component
  []
  [:div.form__input
   [:label.form__label.primary {:for :documentation-link-name
                                :style {:display :block}}
    "Document name"]
   [:input {:type :text
            :id :documentation-link-name
            :name :documentation-link-name
            :on-change #(rf/dispatch [::handlers/set-link-upload-document-name (-> %
                                                                                   .-target
                                                                                   .-value)])}]])

(defn- documentation-link-upload-link-input-component
  []
  [:div.form__input
   [:label.form__label.primary {:for :documentation-link-link
                                :style {:display :block}}
    "Document link (URL)"]
   [:input {:type :text
            :id :documentation-link-link
            :name :documentation-link-link
            :on-change #(rf/dispatch [::handlers/set-link-upload-document-link (-> %
                                                                                   .-target
                                                                                   .-value)])}]])

(defn- documentation-link-upload-form-component
  [task-config-id]
  (let [uploading? @(rf/subscribe [::subs/link-uploading?])]
    [:form#doc-link-upload-form {:action "#"}
     [:div.form__input
      [:fieldset {:disabled? uploading?}
       [:legend
        "Add documentation link"]
       [documentation-link-upload-name-input-component]
       [documentation-link-upload-link-input-component]
       [:br]
       [documentation-link-upload-submit-button-component task-config-id]]]]))

;; View and Container
;; ==================

(defn- back-button-component
  [task-config-id]
  [button/button {:appearance #{:tertiary}
                  :type :button
                  :on-click #(rf/dispatch [:navigate-to [:manatee.task-config.edit.handlers/edit-task :task-config-id task-config-id]])}
   "Back"])

(defn view
  [task-config-id]
  (let [{:keys [task-name]} @(rf/subscribe [::task-config-subs/task-config task-config-id])]
    [:<>
     [notifications-container/alerts {:banner-id :main
                                      :max-alerts 1}]
     [panel/panel {:appearance #{:primary}
                   :class [:task-page__panel]}
      [layout/layout {:appearance #{:column}
                      :class [:add-task-page__form-layout]}
       [:h3
        (str "Task documentation for: " task-name)]
       [:div.form-row.button-group
        [back-button-component task-config-id]]
       [:div.spacer
        [documents-table-component task-config-id]]
       [:div.spacer
        [layout/layout {:appearance #{:row}
                        :class [:task_page__form_layout]}
         [:div.task_page__left-column
          [document-upload-form-component task-config-id]]
         [:div
          [documentation-link-upload-form-component task-config-id]]]]]]]))

(defn view-container
  [_props]
  (let [task-config-id @(rf/subscribe [::task-config-subs/task-config-id])]
    [view task-config-id]))
